import { create } from 'zustand';
import { collection, query, where, onSnapshot, doc, updateDoc, Timestamp, getFirestore, orderBy, limit } from 'firebase/firestore';
import { firebaseApp, firestore } from 'app';
import { toast } from 'sonner';
import brain from 'brain';

export interface AppNotification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'appointment' | 'timesheet' | 'leave' | 'system' | 'info';
  read: boolean;
  actionUrl?: string;
  createdAt: Timestamp;
  metadata?: Record<string, any>;
}

interface NotificationStore {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  subscribeToNotifications: (userId: string) => () => void;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: (userId: string) => Promise<void>;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: true,
  error: null,

  subscribeToNotifications: (userId: string) => {
    if (!userId) {
      console.error('[Notifications] No user ID provided');
      return () => {};
    }

    set({ isLoading: true, error: null });

    const db = firestore;
    const notificationsRef = collection(db, 'notifications');
    
    // Single query: All notifications for this user, ordered by createdAt
    // We'll filter and limit in the client to avoid composite index requirements
    const notificationsQuery = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50) // Limit to last 50 notifications total
    );

    // Subscribe to notifications
    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const allNotifications: AppNotification[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          allNotifications.push({
            id: doc.id,
            userId: data.userId,
            title: data.title,
            message: data.message,
            type: data.type || 'info',
            read: data.read || false,
            actionUrl: data.actionUrl,
            createdAt: data.createdAt,
            metadata: data.metadata,
          });
        });

        // Count unread
        const unreadCount = allNotifications.filter(n => !n.read).length;

        // Show toast for new unread notifications
        const prevUnreadCount = get().unreadCount;
        if (unreadCount > prevUnreadCount && !get().isLoading) {
          const newNotification = allNotifications.find(n => !n.read);
          if (newNotification) {
            toast(newNotification.title, {
              description: newNotification.message,
              duration: 5000,
            });
          }
        }

        set({ 
          notifications: allNotifications, 
          unreadCount,
          isLoading: false,
          error: null 
        });
      },
      (error) => {
        console.error('[Notifications] Error fetching notifications:', error);
        set({ error: error.message, isLoading: false });
      }
    );

    // Return unsubscribe function
    return () => {
      unsubscribe();
    };
  },

  markAsRead: async (notificationId: string) => {
    try {
      // Use the backend API to mark as read
      await brain.mark_notification_as_read({ notificationId });
    } catch (error: any) {
      console.error('[Notifications] Error marking as read:', error);
      toast.error('Failed to mark notification as read');
    }
  },

  markAllAsRead: async (userId: string) => {
    try {
      // Use the backend API to mark all as read
      const response = await brain.mark_all_notifications_as_read();
      const data = await response.json();
      toast.success(data.message || 'All notifications marked as read');
    } catch (error: any) {
      console.error('[Notifications] Error marking all as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  },
}));
